// frontend/components/CreateSurveyForm.js

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createSurvey, fetchSurveyTypes } from "../services/surveyService";
import LoadingBar from "./LoadingBar"; // Import LoadingBar
import LoadingOverlay from "./LoadingOverlay";
import Select from "react-select"; // Import React Select
import "./CreateSurveyForm.css";

const CreateSurveyForm = () => {
  // --------------------------------------------------------------------------
  // STATE
  // --------------------------------------------------------------------------
  const [description, setDescription] = useState("");
  const [selectedSurveyType, setSelectedSurveyType] = useState(null);
  const [selectedLOI, setSelectedLOI] = useState(null);
  const [error, setError] = useState("");
  const [surveyId, setSurveyId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [surveyTypes, setSurveyTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isMenuOpenSurveyType, setIsMenuOpenSurveyType] = useState(false);
  const [isMenuOpenLOI, setIsMenuOpenLOI] = useState(false);

  // ADD THIS STATE
  const [includeDemographics, setIncludeDemographics] = useState(false);

  const navigate = useNavigate();

  // --------------------------------------------------------------------------
  // LOI OPTIONS
  // --------------------------------------------------------------------------
  const loiOptions = [
    {
      label: "1-5 minutes",
      description: "Ideal for quick feedback or short interactions.",
    },
    {
      label: "6-15 minutes",
      description: "Suitable for moderately detailed surveys.",
    },
    {
      label: "16-25 minutes",
      description: "Best for comprehensive surveys requiring in-depth responses.",
    },
  ];

  // --------------------------------------------------------------------------
// CUSTOM STYLES FOR REACT SELECT
// --------------------------------------------------------------------------
const customStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "50px",
    borderColor: state.isFocused ? "var(--dropdown-selected)" : "#ddd",
    boxShadow: state.isFocused
      ? "0 0 0 2px rgba(69, 53, 191, 0.5)" // or var(--dropdown-selected) with alpha
      : "none",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    backgroundColor: "var(--background-color)",
    color: "var(--text-color)",
    cursor: "pointer",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "var(--background-color)",
    color: "var(--dropdown-text)",
    zIndex: 10,
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    borderRadius: "4px",
    marginTop: "4px",
    maxHeight: "300px",
    overflowY: "none",
  }),
  menuList: (provided) => ({
    ...provided,
    padding: 0,
    margin: 0,
    maxHeight: "300px",
    overflowY: "auto",
    backgroundColor: "var(--background-color)",
    color: "var(--dropdown-text)",
  }),
  option: (provided, state) => ({
    ...provided,
    /* If the option is currently selected, use your 'selected' color. */
    backgroundColor: state.isSelected
      ? "var(--dropdown-color)"      // e.g. #4535BF
      : state.isFocused
      ? "var(--dropdown-hover)"         // e.g. #f0f0f0 or #3a3a3a (dark mode)
      : "var(--background-color)",      // default background
    color: state.isSelected
      ? "var(--dropdown-text)" // e.g. #fff
      : "var(--dropdown-text)",         // default text color
    padding: "10px",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "var(--text-color)",
  }),
};

  
  // --------------------------------------------------------------------------
  // CUSTOM LABELS FOR REACT SELECT
  // --------------------------------------------------------------------------
  const formatOptionLabel = ({ title, purpose }) => (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <span style={{ fontWeight: "bold" }}>{title}</span>
      <span style={{ fontSize: "0.9em", color: "#555" }}>{purpose}</span>
    </div>
  );

  const formatOptionLabelLOI = ({ label, description }) => (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <span style={{ fontWeight: "bold" }}>{label}</span>
      <span style={{ fontSize: "0.9em", color: "#555" }}>{description}</span>
    </div>
  );

  // --------------------------------------------------------------------------
  // FETCH SURVEY TYPES ON MOUNT
  // --------------------------------------------------------------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedTypes = await fetchSurveyTypes();
        setSurveyTypes(fetchedTypes);
        setSelectedSurveyType(fetchedTypes[0] || null);
      } catch (err) {
        setError("Failed to load survey types.");
        console.error("Error fetching survey types:", err);
      } finally {
        setLoadingTypes(false);
      }
    };

    fetchData();
  }, []);

  // --------------------------------------------------------------------------
  // HELPER FUNCTION TO MAP PROGRESS TO STATUS TEXT
  // --------------------------------------------------------------------------
  const getProgressStatus = (progress) => {
    if (progress < 20) return "Refining Description";
    if (progress < 40) return "Generating Title";
    if (progress < 60) return "Generating Sections and Questions";
    if (progress < 80) return "Saving Survey to Database";
    if (progress < 100) return "Finalizing";
    return "Complete!";
  };

  // --------------------------------------------------------------------------
  // TRACK SURVEY PROGRESS (SSE)
  // --------------------------------------------------------------------------
  useEffect(() => {
    let source;
    if (isGenerating && surveyId) {
      const backendBaseURL =
        process.env.REACT_APP_API_URL || "http://localhost:8000";
      const token = localStorage.getItem("token");
      const eventSourceURL = `${backendBaseURL}/surveys/progress/${surveyId}?token=${token}`;

      source = new EventSource(eventSourceURL);

      source.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("Received progress update:", data); // Debug log
          if (data.error) {
            setError(data.error);
            source.close();
            setIsGenerating(false);
          }
          if (typeof data.progress === "number" && !isNaN(data.progress)) {
            setProgress(data.progress);
          }
          if (data.progress >= 100) {
            source.close();
            navigate(`/surveys/${surveyId}/details`);
          }
        } catch (parseError) {
          setError("Received malformed progress data.");
          console.error("Progress data parsing error:", parseError);
          source.close();
          setIsGenerating(false);
        }
      };

      source.onerror = (err) => {
        console.error("EventSource failed:", err); // Debug log
        setError("Failed to receive survey progress updates.");
        source.close();
      };
    }

    return () => {
      if (source) source.close(); // Cleanup EventSource
      setProgress(0);
    };
  }, [isGenerating, surveyId, navigate]);

  // --------------------------------------------------------------------------
  // FORM SUBMIT HANDLER
  // --------------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    setIsGenerating(true);
    setProgress(0);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Not authorized, no token provided");
      }

      // Call createSurvey API
      const response = await createSurvey({
        type: selectedSurveyType.title,
        loi: selectedLOI.label,
        description,
        includeDemographics, // This uses the state
      });

      const createdSurveyId = response.surveyId;
      if (createdSurveyId) {
        setSurveyId(createdSurveyId);
      } else {
        throw new Error("Survey ID is undefined in response.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to create survey. Please try again."
      );
      console.error(
        "Survey creation error:",
        err.response?.data || err.message || err
      );
      setIsGenerating(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <div className="create-survey-container">
      <form className="create-survey-form" onSubmit={handleSubmit}>
        <h1>Create a new survey</h1>
        {error && <p className="error-message">{error}</p>}

        {isGenerating ? (
          <div className="survey-creation-loading-overlay" aria-live="polite">
            <div className="survey-creation-loading-content">
              <h2>Generating Your Survey...</h2>
              <LoadingBar progress={progress} />
              <p>
                {typeof progress === "number" && !isNaN(progress)
                  ? progress < 100
                    ? getProgressStatus(progress)
                    : "Complete!"
                  : "Loading..."}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Survey Description */}
            <label htmlFor="description">Survey description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter the survey description"
              required
            />

            {/* Survey Type */}
<label htmlFor="type">Survey type</label>
<div className="input-container">
  <Select
    id="type"
    options={surveyTypes.map((type) => ({
      title: type.title,
      purpose: type.purpose,
    }))}
    value={selectedSurveyType}
    onChange={(selectedOption) => setSelectedSurveyType(selectedOption)}
    placeholder={loadingTypes ? "Loading survey types..." : "Select a survey type"}
    isDisabled={loadingTypes}
    styles={customStyles}
    formatOptionLabel={formatOptionLabel}
    isSearchable={false}
    classNamePrefix="custom"
    menuShouldScrollIntoView={false} // Prevents unnecessary scrolling
    menuPlacement="auto" // Ensures dropdown positions itself optimally
    maxMenuHeight={300} // Limits dropdown height to avoid double scroll
  />
</div>

{/* Length of Interview (LOI) */}
<label htmlFor="loi">Length of Interview (LOI)</label>
<div className="input-container">
  <Select
    id="loi"
    options={loiOptions}
    value={selectedLOI}
    onChange={(selectedOption) => setSelectedLOI(selectedOption)}
    placeholder="Select interview length"
    styles={customStyles}
    formatOptionLabel={formatOptionLabelLOI}
    isSearchable={false}
    classNamePrefix="custom"
    menuShouldScrollIntoView={false}
    menuPlacement="auto"
    maxMenuHeight={300} // Same height limit for consistency
  />
</div>

            {/* Demographics Toggle */}
            <div className="toggle-container">
              <div className="toggle-labels">
                <div className="toggle-title">Include demographic section?</div>
                <p className="toggle-subline">
                  This will add a dedicated Demographic section at the front of your survey.
                </p>
              </div>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  className="toggle-input"
                  checked={includeDemographics}
                  onChange={(e) => setIncludeDemographics(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </div>
            </div>

            {/* Submit Button */}
            <button type="submit" disabled={isSubmitting || loadingTypes}>
              {isSubmitting ? "Creating Survey..." : "Create survey"}
            </button>
          </>
        )}
      </form>
    </div>
  );
};

export default CreateSurveyForm;
